/** Describes metadata stored for subforms. */
export interface ISubformMetadata {
  /**
   * If the subform should be optional.
   *
   * That is, if the parent form can be validated and submitted without needing
   * this subform to be filled.
   *
   * **NOTE:** Subforms will still be validated if at least one of their
   * fields is touched.
   */
  optional?: boolean;
}

/**
 * Decorator factory that will inject necessary metadata for subforms. Must be
 * only used to decorate `FormModel`-type properties in other `FormModel`
 * instances.
 *
 * @param [metadataOptions] - Any options that should be included in the
 * subform's metadata. Must be an {@link ISubformMetadata} object.
 *
 * @returns A decorator function that will inject necessary metadata for a
 * subform property in a form.
 */
export function subform(metadataOptions?: ISubformMetadata) {
  return (
    target: object,
    propertyKey: string,
    descriptor?: PropertyDescriptor
  ) => {
    const { optional = false } = metadataOptions ?? {};

    Reflect.defineProperty(target, propertyKey, {
      value: descriptor?.value,
      writable: true,
      enumerable: true
    });

    // Define a metadata property on the prototype (target)
    if (!Reflect.hasMetadata('subforms:map', target)) {
      Reflect.defineMetadata('subforms:map', new Map(), target);
    }

    const subformsMap: Map<string, ISubformMetadata> = Reflect.getMetadata(
      'subforms:map',
      target
    );

    if (!subformsMap.has(propertyKey)) {
      subformsMap.set(propertyKey, { optional });
    }
  };
}
